import {
  put,
  call,
  all,
  fork,
  select,
  delay,
  takeEvery
} from "redux-saga/effects";

import * as R from "ramda";
import { isEmptyOrNil } from "utils/General";

import {
  actions,
  getters
} from "Portal/NewPortal/Sections/Home/Cards/Tasks/FlyOver/model";

import { eventDetails } from "redux/modules/portal/selectors";

import { apiCall } from "App/Data/sagas";

import * as STANDARD_MODULE_IDS from "@sessionboard/sessionboard-value-types/src/constants/standard-modules";

import getQuery from "redux/modules/modules/utils/get-query";

import { userId as getUserId } from "redux/modules/user/selectors";

import { renderDropZone } from "utils/General/sagas";

import Helpers from "utils/Global/Helpers";

import { DOCUMENT_REQUEST_REVISIONS } from "utils/standard-module-field-ids";

import { getRevisions } from "Portal/NewPortal/Sections/Home/Cards/Tasks/FlyOver/View/Details/FileRequest/selectors";

const { FILES, REVISION_STATUS } = DOCUMENT_REQUEST_REVISIONS;

const processUpload = function*({ file }) {
  const data = yield select(getters.fileUploadData);

  const formatted = {
    ...data,
    uploaded: true,
    url: `https://content-cdn.sessionboard.com/${file[0].key}`,
    is_external: false,
    title: file[0].filename,
    key: file[0].key,
    filename: file[0].filename,
    mimetype: file[0].mimetype,
    size: file[0].size,
    payload: file[0],
    thumbnail_url: Helpers.isImage(file[0].mimetype)
      ? `https://content-cdn.sessionboard.com/${file[0].key}`
      : data?.thumbnail_url
  };

  yield put(actions.setFileUploadData(formatted));
};

const initFileUploadSagas = function*({
  payload: {
    onFileUploadProgress,
    onFileUploadCancel,
    onFileUploadFailed,
    onFileUploadFinished,
    onFileSelected
  }
}) {
  try {
    const dropzoneContainer = document.getElementById("dropzone-container");

    if (dropzoneContainer) {
      dropzoneContainer.innerHTML = "";

      const file = yield call(renderDropZone, {
        customText:
          "Drag and drop a file here or click to choose one from your computer. ",
        multiple: false,
        fromSources: ["local_file_system"],
        container: "dropzone-container",
        accept: ["image/*", "video/*", "application/*"],
        storeTo: {
          path: "content/"
        },
        uploadConfig: {
          progressInterval: 500
        },
        onFileUploadProgress,
        onFileUploadCancel,
        onFileUploadFailed,
        onFileUploadFinished,
        onFileSelected
      });

      yield call(processUpload, { file });

      document.getElementById("dropzone-container").innerHTML = "";
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(e);
  }
};

const fetchRecord = function*({
  moduleId = STANDARD_MODULE_IDS.documentRequests.id,
  fetchedRecord,
  query
}) {
  const userId = yield select(getUserId);
  const { record = {} } = yield call(apiCall, {
    method: "get",
    url: `/modules/${moduleId}/record/${fetchedRecord.id}${query || ""}`,
    qs: {
      userId
    }
  });

  const recordData = R.pathOr({}, ["record"], record);

  yield put(actions.setRecord(recordData));

  const files = yield select(getRevisions);

  yield put(actions.setFiles(files));

  yield put(actions.initMessages());
};

const initFileRequest = function*({
  payload: { taskData = undefined, taskSessionId }
}) {
  if (isEmptyOrNil(taskData)) {
    return;
  }

  try {
    yield all([
      put(actions.setLoadingFileUpload(true)),
      put(actions.cleanFileUploadState())
    ]);

    const { meta = {} } = taskData || {};

    const eventDetailss = yield select(eventDetails);
    const { id: eventId } = eventDetailss || {};

    const {
      // dueDateWithExtendedDeadline = null,
      requestTypeId = null,
      accountId = null,
      contactId = null
    } = meta || {};

    const moduleId = STANDARD_MODULE_IDS.documentRequests.id;

    const { payload: fetchedRecord } = yield call(apiCall, {
      method: "post",
      url: `/file-requests/event/${eventId}/request/${requestTypeId}/submission/${
        taskSessionId
          ? `session/${taskSessionId}`
          : accountId
          ? `account/${accountId}`
          : `contact/${contactId}`
      }`,
      data: taskSessionId
        ? {
            accountId,
            contactId
          }
        : {}
    });

    const options = {
      eventId
    };
    const query = getQuery(options);

    if (fetchedRecord?.id) {
      yield call(fetchRecord, { moduleId, fetchedRecord, query });
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(e);
  } finally {
    yield put(actions.setLoadingFileUpload(false));
  }
};

const onFileSelected = function*({ payload: file }) {
  if (!isEmptyOrNil(file)) {
    yield put(actions.setFileUploadData(file));
    yield put(actions.setFileUploadUploadHasFinished(false));
  }
};

const onProgress = function*(data) {
  yield put(actions.setFileUploadProgress(data.totalPercent));
};
// @TODO: remove the delay and fix with a local state in the component or selector
const onFinish = function*() {
  yield put(actions.setFileUploadProgress(100));
  yield delay(2000);
  yield put(actions.setFileUploadUploadHasFinished(true));
};

const cancelUpload = function*({}) {
  // Create the event
  const event = new CustomEvent("renderDropZone-cancel-upload");

  yield call([document, document.dispatchEvent], event);
};

const onFileUploadCancel = function*() {
  yield put(actions.cleanFileUploadState());
};

const saveFileUpload = function*({ payload: { record, file } }) {
  const eventDetailss = yield select(eventDetails);
  const { id: eventId } = eventDetailss || {};
  const options = {
    eventId
  };

  yield put(actions.setLoadingSavingFile(true));

  const query = getQuery(options);

  const userId = yield select(getUserId);

  try {
    yield call(apiCall, {
      method: "post",
      url: `/modules/${
        STANDARD_MODULE_IDS.documentRequestRevisions.id
      }/records${query || ""}`,
      data: {
        moduleId: STANDARD_MODULE_IDS.documentRequestRevisions.id,
        record: {
          requestRecordId: record.id,
          [FILES]: {
            type: "file",
            value: {
              files: [R.omit(["payload"], file)]
            }
          },
          [REVISION_STATUS]: {
            type: "dropdown",
            value: { records: [{ value: "Pending" }] }
          }
        }
      },
      qs: {
        userId
      }
    });

    // refresh data
    const taskData = yield select(getters.taskData);
    const taskSessionId = yield select(getters.taskSessionId);

    yield put(actions.refreshData({ taskData, taskSessionId }));
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(e);
  } finally {
    yield put(actions.setLoadingSavingFile(false));
  }
};

const watchOnProgress = function*() {
  yield takeEvery(actions.onFileUploadProgress.type, onProgress);
};

const watchOnFinished = function*() {
  yield takeEvery(actions.onFileUploadFinished.type, onFinish);
};
const watchCancelUpload = function*() {
  yield takeEvery(actions.cancelFileUpload.type, cancelUpload);
};

const watchInitFileRequest = function*() {
  yield takeEvery(actions.initFileRequest.type, initFileRequest);
};

const watchInitFileUpload = function*() {
  yield takeEvery(actions.initFileUpload.type, initFileUploadSagas);
};

const watchOnFileSelected = function*() {
  yield takeEvery(actions.onFileSelected.type, onFileSelected);
};

const watchOnFileUploadCancel = function*() {
  yield takeEvery([actions.onFileUploadCancel.type], onFileUploadCancel);
};

const watchSaveFileUpload = function*() {
  yield takeEvery([actions.saveFileUpload.type], saveFileUpload);
};

const rootSaga = function*() {
  yield all([
    fork(watchOnFileSelected),
    fork(watchInitFileUpload),
    fork(watchOnFileUploadCancel),
    fork(watchInitFileRequest),
    fork(watchCancelUpload),
    fork(watchOnProgress),
    fork(watchOnFinished),
    fork(watchSaveFileUpload)
  ]);
};

export default rootSaga;
